import React, { useEffect, useState } from "react";
import { api, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import { Truck, Save, X, Check } from "lucide-react";

export default function PendingMT() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [mtValues, setMtValues] = useState({});  // item_index -> mt_qty
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pending/mt");
      setRows(data.rows || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openRow = (row) => {
    setOpenId(row.id);
    const init = {};
    row.items.forEach((it, i) => { init[i] = it.filled_qty; });  // default MT = filled sent
    setMtValues(init);
  };

  const cancelOpen = () => { setOpenId(null); setMtValues({}); };

  const saveMt = async (row) => {
    setBusy(true);
    try {
      const items = row.items.map((it, i) => ({
        brand: it.brand, size: it.size, mt_qty: Number(mtValues[i] || 0),
      }));
      await api.post(`/transactions/${row.id}/mt-return`, {
        items, return_date: todayISO(),
      });
      toast.success("MT recorded · stock updated");
      cancelOpen();
      load();
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("This entry no longer exists. Refreshing list…");
        cancelOpen();
        load();
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="pending-mt-page">
      <div className="card-flat p-5 flex items-center gap-4">
        <Truck size={24} className="text-gray-500" />
        <div>
          <div className="small-label text-gray-500">In-Transit Returns</div>
          <div className="font-heading font-black text-lg sm:text-2xl">
            {rows.length} vehicle{rows.length === 1 ? "" : "s"} pending MT entry
          </div>
          <div className="text-sm text-gray-600">
            When vehicle returns to godown, open the row and record MT cylinders received.
          </div>
        </div>
      </div>

      <div className="card-flat">
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Dispatch</th>
                <th className="text-left">Challan</th>
                <th className="text-left">Customer</th>
                <th className="text-left">Driver / Vehicle</th>
                <th className="text-left">Filled sent</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="py-6 text-center text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="6" className="py-10 text-center text-gray-400 text-sm">
                  All vehicles accounted for 🎉
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className={openId === r.id ? "bg-[#FEF3C7]" : ""} data-testid={`pending-mt-row-${r.id}`}>
                    <td className="data-num text-xs">{r.date}</td>
                    <td className="data-num text-xs font-bold">{r.challan_no || "—"}</td>
                    <td className="font-semibold">{r.party_name}</td>
                    <td className="text-xs text-gray-600">
                      {r.driver_name || "—"}{r.vehicle_no ? ` · ${r.vehicle_no}` : ""}
                    </td>
                    <td className="text-xs">
                      <div className="flex flex-wrap gap-1.5">
                        {r.items.map((it, i) => (
                          <span key={i} className="stock-filled px-2 py-0.5 rounded-sm">
                            {it.brand} {it.size} × {it.filled_qty}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="text-right">
                      {openId === r.id ? (
                        <button onClick={cancelOpen} className="text-sm font-semibold text-gray-500 hover:text-black inline-flex items-center gap-1">
                          <X size={14} /> Cancel
                        </button>
                      ) : (
                        <button onClick={() => openRow(r)} className="btn-primary h-9 px-3 text-xs inline-flex items-center gap-1" data-testid={`record-mt-${r.id}`}>
                          <Check size={14} /> Record MT
                        </button>
                      )}
                    </td>
                  </tr>
                  {openId === r.id && (
                    <tr className="bg-[#FEF3C7]/60">
                      <td colSpan="6" className="p-5">
                        <div className="small-label text-gray-600 mb-3">
                          MT cylinders received from {r.party_name}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {r.items.map((it, i) => (
                            <div key={i} className="card-flat p-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-semibold">{it.brand} {it.size}</div>
                                <div className="text-xs text-gray-500">sent: <span className="data-num font-bold">{it.filled_qty}</span></div>
                              </div>
                              <input type="number" className="input-lg h-10 text-right data-num"
                                value={mtValues[i] ?? 0}
                                onChange={(e) => setMtValues({ ...mtValues, [i]: e.target.value })}
                                data-testid={`mt-input-${r.id}-${i}`} />
                              <div className="text-xs mt-1.5">
                                {Number(mtValues[i] || 0) < it.filled_qty ? (
                                  <span className="text-[#B45309] font-semibold">
                                    {it.filled_qty - Number(mtValues[i] || 0)} MT short · will stay as pending cylinders
                                  </span>
                                ) : Number(mtValues[i] || 0) === it.filled_qty ? (
                                  <span className="text-[#15803D] font-semibold">Fully returned</span>
                                ) : (
                                  <span className="text-gray-500">Extra MT received (+{Number(mtValues[i] || 0) - it.filled_qty})</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-end mt-4">
                          <button onClick={() => saveMt(r)} disabled={busy} className="btn-primary h-11 flex items-center gap-2" data-testid={`save-mt-${r.id}`}>
                            <Save size={16} /> {busy ? "Saving…" : "Save MT & Update Stock"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
